<template>
    <DialogBase class="dialog-share">

        <template #title>
            Share&nbsp;{{ name }}
        </template>

        <template #content>

            <div v-if="isSubscriptionFree" class="announcement">
                <p>Sharing cannot be managed in the free subscription.<br />All generated assets are shared with the community.</p>
                <v-btn 
                    elevation="0" 
                    @click="handleSelect">
                    Select Different Plan
                </v-btn>
            </div>
            
            <div v-else>
                <div class="options">
                    <v-checkbox 
                        hide-details 
                        class="checkbox" 
                        color="color1" 
                        label="Enable link sharing" 
                        v-model="shared" 
                        :disabled="store">
                    </v-checkbox>
                    <v-checkbox 
                        v-if="isAdministrator"
                        hide-details 
                        class="checkbox" 
                        color="color1" 
                        label="Show in store" 
                        v-model="store">
                    </v-checkbox>
                </div>
            </div>

            <div class="link">
                <div class="url">{{ link }}</div>
                <div class="copy">
                    <v-btn 
                        color="color1" 
                        :disabled="!shared" 
                        :loading="loading" 
                        @click="handleCopy">
                        <v-icon icon="fa-solid fa-copy"></v-icon>
                    </v-btn>
                </div>
            </div>

            <Transition name="qr-fade-scale">
                <div v-if="shared" class="qr-code">
                    <img :src="qrCodeUrl" alt="QR code for share link" />
                </div>
            </Transition>

        </template>

        <template #actions>
            <v-btn 
                variant="elevated" 
                elevation="2" 
                color="surface" 
                @click="close">
                Close
            </v-btn>
        </template>

    </DialogBase>
</template>

<script>
import DEPLOYMENT from '@generio-ai/library-legacy/constants/deployment'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import ROUTES from '@generio-ai/library-legacy/constants/routes'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { DialogBase } from '@generio-ai/library-legacy/components/dialogs'
import QRCode from 'qrcode'

export default {
    name: 'DialogShare',
    components: {
        DialogBase
    },
    props: {
        asset: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            shared: false,
            store: false,
            qrCodeUrl: ''
        }
    },
    computed: {
        ...mapGetters('auth', [
            'isAdministrator'
        ]),
        ...mapGetters('assets', [
            'isTypeText',
            'isTypeImage',
            'isTypeModel'
        ]),
        ...mapGetters('subscriptions', [
            'isSubscriptionFree'
        ]),
        ...mapGetters('deployment', [
            'getUrlForClient'
        ]),
        name() {
            if (this.isTypeText(this.asset)) return 'Text'
            if (this.isTypeImage(this.asset)) return 'Image'
            if (this.isTypeModel(this.asset)) return 'Model'
            return ''
        },
        url() {
            return this.getUrlForClient(DEPLOYMENT.CLIENT.SHARE) + '/?id=' + this.asset.id
        },
        link() {
            if (this.asset.shared) 
            {
                return this.getUrlForClient(DEPLOYMENT.CLIENT.SHARE) + '/?id=' + this.asset.id
            }
            return 'Sharing not activated'
        },
        sharedCalculated() {
            if (this.store) return 3
            if (this.shared) return 1
            return 0
        },
        shareLabel() {
            return this.name ? this.name.toLowerCase() : 'asset'
        }
    },
    watch: {
        'asset.shared'(newVal, oldVal) {
            if (newVal === oldVal) return
            this.shared = newVal > 0
            this.store = newVal === 3
            if (newVal > 0) {
                this.generateQrCode()
            } else {
                this.qrCodeUrl = ''
            }
        },
        shared(newVal, oldVal) {
            if (newVal === oldVal) return
            this.updateSharing()
            if (!newVal) {
                this.qrCodeUrl = ''
            }
        },
        store(newVal, oldVal) {
            if (newVal === oldVal) return
            if (newVal) this.shared = true
            this.updateSharing()
            if (!newVal && !this.shared) {
                this.qrCodeUrl = ''
            }
        }
    },
    methods: 
    {
        ...mapActions('assets', [
            'updateShared'
        ]),
        ...mapActions('dialogs', [
            'close'
        ]),
        ...mapActions('notifications', [
            'show'
        ]),
        ...mapActions('router', [
            'openRoute'
        ]),
        handleClose()
        {
            this.close()
        },
        updateSharing()
        {
            // avoid unnecessary calls
            if (this.sharedCalculated === this.asset.shared) return

            // set loading state
            this.loading = true

            this.updateShared({
                asset: this.asset,
                shared: this.sharedCalculated
            })
            .finally(() => {
                this.loading = false
            })
        },
        handleCopy() 
        {
            // only copy if sharing is activated
            if (!this.asset.shared) return

            // write into clipboard
            navigator.clipboard.writeText(this.link)
            .then(() => {
                this.show({
                    message: 'Link successfully copied to the clipboard.',
                    level: LEVELS.INFORMATION
                })
            })
            .catch(() => {
                this.showToast({
                    message: 'Unable to copy link to the clipboard.',
                    level: LEVELS.ERROR
                })
            })
        },
        handleSelect()
        {
            this.openRoute({ route: ROUTES.ACCOUNT.SUBSCRIPTION })
        },
        async generateQrCode()
        {
            if (!this.asset.shared) {
                this.qrCodeUrl = ''
                return
            }

            try {
                this.qrCodeUrl = await QRCode.toDataURL(this.url, {
                    margin: 1,
                    width: 256,
                    color: {
                        dark: '#000000ff',
                        light: '#ffffffff'
                    }
                })
            }
            catch (error) {
                console.error('Unable to generate QR code for asset share link.', error)
                this.qrCodeUrl = ''
                this.show({
                    message: 'Unable to generate QR code for the share link.',
                    level: LEVELS.ERROR
                })
            }
        }
    },
    mounted() {
        this.shared = this.asset.shared > 0
        this.store = this.asset.shared === 3
        if (this.asset.shared) {
            this.generateQrCode()
        }
    }
}
</script>

<style scoped>
.qr-fade-scale-enter-active,
.qr-fade-scale-leave-active {
    transition: opacity 250ms ease, transform 250ms ease;
}

.qr-fade-scale-enter-from,
.qr-fade-scale-leave-to {
    opacity: 0;
    transform: scaleY(0);
}

.qr-fade-scale-enter-to,
.qr-fade-scale-leave-from {
    opacity: 1;
    transform: scaleY(1);
}

.dialog-share .announcement {
    width: 100%;
    padding-top: 10px;
    padding-bottom: 20px;
    margin-bottom: 30px;
    border-radius: 8px;
    text-align: center;
    font-size: 1.1rem;
    color: rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-error), 0.2);
}

.dialog-share .announcement .v-btn {
    color: rgb(var(--v-theme-primary));
}

.dialog-share .options {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.dialog-share .checkbox {
    display: flex;
    justify-content: center;
}

.dialog-share .link {
    width: 100%;
    display: flex;
    margin-top: 10px;
    margin-bottom: 10px;
}

.dialog-share  .url {
    display: flex;
    flex-grow: 1;
    color: rgb(var(--v-theme-primary));
    background: rgb(var(--v-theme-darkerbase));
    margin-right: 10px;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-size: 13px;
     -webkit-user-select: all;
    -moz-user-select: all;
    -ms-user-select: all;
    user-select: all;
}

.dialog-share .copy {
    flex-shrink: 1;
}

.dialog-share .qr-code {
    width: 100%;
    height: 200px;
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: linear-gradient(
        135deg,
        rgba(var(--v-theme-color1), 1.0) 0%,
        rgba(var(--v-theme-color2), 1.0) 50%,
        rgba(var(--v-theme-color3), 1.0) 100%
    );
    overflow: hidden;
    transform-origin: center;
}

.dialog-share img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
}

@media (max-width: 600px) {
    .dialog-share .options {
        flex-direction: column;
        gap: 0px;
    }
}
</style>